import { useApolloClient, useMutation } from '@apollo/client/react';
import { Badge, Box, Button, HStack, Separator, Text, VStack, Wrap } from '@chakra-ui/react';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { DraftFastAgreementButton } from '@src/components/sco/Advisors/shared/DraftFastAgreementButton';
import { PaywalledAdvisorAction } from '@src/components/sco/Advisors/shared/PaywalledAdvisorAction';
import { SpecialtyTag } from '@src/components/sco/Advisors/shared/SpecialtyTag';
import { DECLINE_BOARD_INVITE_CONFIRM, END_BOARD_SEAT_CONFIRM, WITHDRAW_BOARD_INVITE_CONFIRM, engagementLifecycle } from '@src/components/sco/Advisors/utils/engagementLifecycle';
import { engagementChatHref, engagementDisplayName } from '@src/components/sco/Advisors/utils/mapEngagements';
import { founderDisplayName, founderUserIdOf } from '@src/components/sco/Advisors/utils/mapFounderEngagements';
import { refreshAdvisorBoard } from '@src/components/sco/Advisors/utils/refreshAdvisorBoard';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ADVISOR_ENGAGEMENT_STATUS_ACTIVE, ADVISOR_ENGAGEMENT_STATUS_ENDED, ADVISOR_ENGAGEMENT_STATUS_INVITED, ADVISOR_ENGAGEMENT_STATUS_SEARCHING } from '@src/constants/advisor-engagement-statuses';
import { END_ADVISOR_ENGAGEMENT } from '@src/gql/mutations/EndAdvisorEngagement';
import { RESPOND_TO_BOARD_INVITE } from '@src/gql/mutations/RespondToBoardInvite';
import { WITHDRAW_BOARD_INVITE } from '@src/gql/mutations/WithdrawBoardInvite';
import { ADVISOR_FOUNDER_ENGAGEMENTS } from '@src/gql/queries/AdvisorFounderEngagements';
import { ScoAdvisorEngagement } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import moment from 'moment/moment';
import { useState } from 'react';
import { LuCalendar, LuCheck, LuMessageSquare, LuPencil, LuSearch, LuSparkles, LuX } from 'react-icons/lu';
const statusPalette = (statusKey?: string | null) => {
  switch (statusKey) {
    case ADVISOR_ENGAGEMENT_STATUS_ACTIVE:
      return 'green';
    case ADVISOR_ENGAGEMENT_STATUS_INVITED:
      return 'orange';
    case ADVISOR_ENGAGEMENT_STATUS_SEARCHING:
      return 'blue';
    case ADVISOR_ENGAGEMENT_STATUS_ENDED:
      return 'red';
    default:
      return 'gray';
  }
};

/**
 * Date only, `null` when absent — engagement milestones read as dates, not
 * timestamps. Distinct from `utils/sessionFormat`'s `formatWhen`, which renders
 * a date **and** time for sessions.
 */
const formatDay = (value?: string | null) => {
  if (!value) {
    return null;
  }
  const m = moment(value);
  return m.isValid() ? m.format('MMM D, YYYY') : null;
};
type ManageEngagementPanelProps = {
  engagement: ScoAdvisorEngagement;
  /** Which party the aside is showing a profile for (affects primary chat CTA). */
  profilePerspective: 'advisor' | 'founder';
  /** Hide category / why / timeline when the host page already shows them. */
  showDetails?: boolean;
  /** Hide “View board seat” when this panel is already on that page. */
  hideSeatLink?: boolean;
};
export const ManageEngagementPanel = ({
  engagement,
  profilePerspective,
  showDetails = true,
  hideSeatLink = false
}: ManageEngagementPanelProps) => {
  const paths = useAdvisorsPaths();
  const {
    currentUser
  } = useCurrentUser();
  const apolloClient = useApolloClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const [ending, setEnding] = useState(false);
  const userId = currentUser?.userId;
  const advisorName = engagementDisplayName(engagement);
  const lifecycle = engagementLifecycle(engagement, userId, advisorName);
  const {
    isFounder,
    isAdvisor,
    isAI,
    isTeam,
    canEditSeat,
    canWithdrawInvite,
    canEndSeat,
    canRespondToInvite,
    canOpenChat,
    canFindAdvisors,
    canReviewApplications,
    applicationCount,
    waitingOnLabel
  } = lifecycle;
  const statusKey = engagement.statusKey;
  const statusTitle = engagement.status?.title || statusKey || 'Unknown';
  const advisorUserId = engagement.advisorUserId || engagement.advisorUser?.userId;
  const founderUserId = founderUserIdOf(engagement);
  const seatId = engagement.advisorEngagementId;
  const groupRequestId = engagement.groupRequestId || engagement.groupRequest?.groupRequestId;
  const skills = (engagement.skillTagTitles || []).filter(Boolean) as string[];
  const [endAdvisorEngagement, endMutation] = useMutation(END_ADVISOR_ENGAGEMENT);
  const [respondToBoardInvite, respondMutation] = useMutation(RESPOND_TO_BOARD_INVITE);
  const [withdrawBoardInvite, withdrawMutation] = useMutation(WITHDRAW_BOARD_INVITE);
  const handleEnd = async () => {
    setActionError(null);
    if (!window.confirm(END_BOARD_SEAT_CONFIRM)) {
      return;
    }
    setEnding(true);
    try {
      await endAdvisorEngagement({
        variables: {
          input: {
            advisorEngagementId: engagement.advisorEngagementId
          }
        }
      });
      await refreshAdvisorBoard(apolloClient);
      await apolloClient.refetchQueries({
        include: [ADVISOR_FOUNDER_ENGAGEMENTS]
      });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not end engagement.');
    } finally {
      setEnding(false);
    }
  };
  const handleInviteResponse = async (action: 'ACCEPT' | 'DECLINE') => {
    if (action === 'DECLINE' && !window.confirm(DECLINE_BOARD_INVITE_CONFIRM)) {
      return;
    }
    setActionError(null);
    try {
      await respondToBoardInvite({
        variables: {
          input: {
            advisorEngagementId: engagement.advisorEngagementId,
            action
          }
        }
      });
      await refreshAdvisorBoard(apolloClient);
      await apolloClient.refetchQueries({
        include: [ADVISOR_FOUNDER_ENGAGEMENTS]
      });
      if (action === 'ACCEPT') {
        const founderUserId_0 = founderUserIdOf(engagement);
        void window.location.assign(paths.withQuery(paths.inbox, {
          founderUserId: founderUserId_0 || undefined
        }));
      }
    } catch (err_0) {
      setActionError(err_0 instanceof Error ? err_0.message : 'Could not update invite.');
    }
  };
  const handleWithdraw = async () => {
    if (!window.confirm(WITHDRAW_BOARD_INVITE_CONFIRM)) {
      return;
    }
    setActionError(null);
    try {
      await withdrawBoardInvite({
        variables: {
          input: {
            advisorEngagementId: engagement.advisorEngagementId
          }
        }
      });
      await refreshAdvisorBoard(apolloClient);
      await apolloClient.refetchQueries({
        include: [ADVISOR_FOUNDER_ENGAGEMENTS]
      });
    } catch (err_1) {
      setActionError(err_1 instanceof Error ? err_1.message : 'Could not withdraw invite.');
    }
  };
  const invitedAt = formatDay(engagement.invitedAt);
  const activatedAt = formatDay(engagement.activatedAt);
  const endedAt = formatDay(engagement.endedAt);
  const chatActive = formatDay(engagement.chatLastActiveAt || engagement.chatThread?.lastActiveAt);
  return <VStack align={'stretch'} gap={4} width={'full'}>
      <VStack align={'stretch'} gap={2}>
        <Text color={'colorPalette.fg/50'} textStyle={'labelXs'}>
          Engagement
        </Text>
        <HStack gap={2} wrap={'wrap'}>
          <Badge colorPalette={statusPalette(statusKey)} variant={'subtle'}>
            {statusTitle}
          </Badge>
          {engagement.source?.title || engagement.sourceKey ? <Badge colorPalette={'gray'} variant={'outline'}>
              {engagement.source?.title || engagement.sourceKey}
            </Badge> : null}
          {isAI ? <Badge colorPalette={'green'} variant={'subtle'}>
              Free · Always on
            </Badge> : null}
        </HStack>
        <Text color={'colorPalette.fg/70'} fontSize={'sm'}>
          {isFounder && (engagement.advisorUserId || engagement.advisorUser || engagement.isAI) ? `With ${advisorName}` : isFounder ? 'No advisor assigned yet' : isAdvisor ? `With ${founderDisplayName(engagement)}` : 'Board engagement'}
        </Text>
        {waitingOnLabel ? <Text color={'colorPalette.fg/70'} fontSize={'sm'}>
            {waitingOnLabel}
          </Text> : null}
      </VStack>

      {showDetails && (engagement.category?.title || engagement.whyText || skills.length > 0) && <VStack align={'stretch'} bg={'colorPalette.panel'} border={'1px solid'} borderColor={'colorPalette.border/40'} borderRadius={'lg'} gap={3} padding={3}>
          {engagement.category?.title ? <Box>
              <Text color={'colorPalette.fg/50'} textStyle={'label2xs'}>
                Category
              </Text>
              <SpecialtyTag colorPalette={'purple'} label={engagement.category.title} />
            </Box> : null}
          {engagement.whyText ? <Box>
              <Text color={'colorPalette.fg/50'} marginBottom={1} textStyle={'label2xs'}>
                Why
              </Text>
              <Text color={'colorPalette.fg/80'} fontSize={'sm'} lineClamp={5}>
                {engagement.whyText}
              </Text>
            </Box> : null}
          {skills.length > 0 ? <Box>
              <Text color={'colorPalette.fg/50'} marginBottom={1} textStyle={'label2xs'}>
                Skills
              </Text>
              <Wrap gap={1.5}>
                {skills.slice(0, 8).map(s => <SpecialtyTag key={s} colorPalette={'cyan'} label={s} />)}
              </Wrap>
            </Box> : null}
          {engagement.company?.name ? <Box>
              <Text color={'colorPalette.fg/50'} textStyle={'label2xs'}>
                Company
              </Text>
              <Text fontSize={'sm'} fontWeight={'medium'}>
                {engagement.company.name}
              </Text>
            </Box> : null}
        </VStack>}

      {showDetails ? <VStack align={'stretch'} gap={1.5}>
          <Text color={'colorPalette.fg/50'} textStyle={'labelXs'}>
            Timeline
          </Text>
          {invitedAt && <Text color={'colorPalette.fg/70'} fontSize={'xs'}>
              Invited {invitedAt}
            </Text>}
          {activatedAt && <Text color={'colorPalette.fg/70'} fontSize={'xs'}>
              Active since {activatedAt}
            </Text>}
          {endedAt && <Text color={'colorPalette.fg/70'} fontSize={'xs'}>
              Ended {endedAt}
            </Text>}
          {chatActive && <Text color={'colorPalette.fg/70'} fontSize={'xs'}>
              Last chat activity {chatActive}
            </Text>}
          {!invitedAt && !activatedAt && !endedAt && !chatActive && <Text color={'colorPalette.fg/50'} fontSize={'xs'}>
              No timeline events yet
            </Text>}
        </VStack> : null}

      <Separator />

      <VStack align={'stretch'} gap={2}>
        <Text color={'colorPalette.fg/50'} textStyle={'labelXs'}>
          Actions
        </Text>

        {canOpenChat && <PaywalledAdvisorAction isTeamAdvisor={profilePerspective === 'advisor' && isTeam} upgradeCtaButtonProps={{
        size: 'sm',
        width: 'full'
      }}>
            <Button asChild size={'sm'} variant={'cta'} width={'full'}>
              <NextLink href={paths.withQuery(paths.inbox, {
            advisorUserId: profilePerspective === 'advisor' ? advisorUserId : undefined,
            founderUserId: profilePerspective === 'founder' ? founderUserId : undefined
          })}>
                <LuMessageSquare />
                Message in Inbox
              </NextLink>
            </Button>
          </PaywalledAdvisorAction>}

        {canWithdrawInvite && <Button loading={withdrawMutation.loading} size={'sm'} variant={'outline'} width={'full'} onClick={() => void handleWithdraw()}>
            Withdraw invite
          </Button>}

        {isAI && <Button asChild size={'sm'} variant={'cta'} width={'full'}>
            <NextLink href={engagementChatHref(engagement, paths.base)}>
              <LuSparkles />
              Open Wil chat
            </NextLink>
          </Button>}

        {isFounder && seatId && !hideSeatLink && <Button asChild size={'sm'} variant={'outline'} width={'full'}>
            <NextLink href={`${paths.seats}/${seatId}`}>
              <LuCalendar />
              View board seat
            </NextLink>
          </Button>}

        {canFindAdvisors && <Button asChild size={'sm'} variant={'cta'} width={'full'}>
            <NextLink href={paths.directory}>
              <LuSearch />
              Find advisors to invite
            </NextLink>
          </Button>}

        {canReviewApplications && seatId && !hideSeatLink && <Button asChild size={'sm'} variant={'outline'} width={'full'}>
            <NextLink href={`${paths.seats}/${seatId}`}>
              Review {applicationCount === 1 ? '1 application' : `${applicationCount} applications`}
            </NextLink>
          </Button>}

        {canEditSeat && seatId && <Button asChild size={'sm'} variant={'outline'} width={'full'}>
            <NextLink href={`${paths.seats}/${seatId}/edit`}>
              <LuPencil />
              Edit seat
            </NextLink>
          </Button>}

        {isFounder && groupRequestId && statusKey === ADVISOR_ENGAGEMENT_STATUS_SEARCHING && <Text color={'colorPalette.fg/60'} fontSize={'xs'}>
            {engagement.notifyMatches ? 'Recruiting is on — matches are being generated for this seat.' : 'Recruiting is off for this seat.'}
          </Text>}

        {canEditSeat && <DraftFastAgreementButton showError engagement={engagement} variant={'outline'} width={'full'} />}

        {canRespondToInvite && <HStack gap={2}>
            <Button flex={1} loading={respondMutation.loading} size={'sm'} variant={'cta'} onClick={() => handleInviteResponse('ACCEPT')}>
              <LuCheck />
              Accept
            </Button>
            <Button flex={1} loading={respondMutation.loading} size={'sm'} variant={'outline'} onClick={() => handleInviteResponse('DECLINE')}>
              <LuX />
              Decline
            </Button>
          </HStack>}

        {canEndSeat && <Button colorPalette={'red'} loading={ending || endMutation.loading} size={'sm'} variant={'outline'} width={'full'} onClick={handleEnd}>
            End seat
          </Button>}

        {isAI && isFounder && <Text color={'colorPalette.fg/50'} fontSize={'xs'} textAlign={'center'}>
            Wil.ai is always on for your account and cannot be ended.
          </Text>}

        {isTeam && isFounder && <Text color={'colorPalette.fg/50'} fontSize={'xs'} textAlign={'center'}>
            Startups.com team advisors are managed with your plan and cannot be ended here.
          </Text>}
      </VStack>

      {actionError && <Text color={'red.fg'} fontSize={'sm'}>
          {actionError}
        </Text>}
    </VStack>;
};